import "./EventList.css";

import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import Modal, { ModalSize } from "../../../misc/components/modal";
import {
    CloseEventListDialogAction,
    JumpToTimePointAction,
    RemovePeriodEventAction,
    RemovePointEventAction
} from "../actions";
import { AnalyseState } from "../interfaces/state";
import { PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import { EventListTable } from "./EventListTable";
import { EventTotals } from "./EventTotals";
import { Query } from '../../../misc/components/queryTable/Query';
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";

export interface EventListProps {
    isVisible: boolean;
    pointEvents: PeaksPoint[];
    periodEvents: PeaksPeriod[];
    tableReRenderHack: string;

    dispatch?: Dispatch<AnalyseState>;
}

export const EventList = (props: EventListProps) => {
    const [periodTableQuery, setPeriodTableQuery] = React.useState<Query<any>>();
    const [pointTableQuery, setPointTableQuery] = React.useState<Query<any>>();
    const legacyClient = useWebPortalLegacyClient();

    if (props.isVisible) {

        return (
            <Modal
                show={true}
                size={ModalSize.Large}
                header="Event List"
                closeModalHandler={() =>
                    props.dispatch(CloseEventListDialogAction.create())
                }>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-12">
                            <EventTotals
                                pointEvents={props.pointEvents}
                                periodEvents={props.periodEvents}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                                <h3 className="text-primary">
                                    Individual Period Events
                                </h3>
                                <ErrorBoundary>
                                    <EventListTable
                                        key={props.tableReRenderHack}
                                        startTimeColumnName="Start Time"
                                        timestampColumn="startTime"
                                        withEndTime={true}
                                        events={props.periodEvents}
                                        onJumpToEventClick={timepoint => {
                                            props.dispatch(
                                                JumpToTimePointAction.create(
                                                    timepoint
                                                )
                                            );
                                            props.dispatch(
                                                CloseEventListDialogAction.create()
                                            );
                                        }}
                                        onDeleteEventClick={id =>
                                            props.dispatch(
                                                RemovePeriodEventAction.create(
                                                    legacyClient,
                                                    id
                                                )
                                            )
                                        }
                                    query={periodTableQuery}
                                    />
                                </ErrorBoundary>
                            </div>
                        <div className="col-md-6">
                                <h3 className="text-primary">
                                    Individual Point Events
                                </h3>
                                <ErrorBoundary>
                                    <EventListTable
                                        key={props.tableReRenderHack}
                                        startTimeColumnName="Waveform Time"
                                        timestampColumn="timestamp"
                                        withEndTime={false}
                                        events={props.pointEvents}
                                        renderRawTime
                                        onJumpToEventClick={timepoint => {
                                            props.dispatch(
                                                JumpToTimePointAction.create(
                                                    timepoint
                                                )
                                            );
                                            props.dispatch(
                                                CloseEventListDialogAction.create()
                                            );
                                        }}
                                        onDeleteEventClick={id =>
                                            props.dispatch(
                                                RemovePointEventAction.create(
                                                    legacyClient,
                                                    id
                                                )
                                            )
                                        }
                                        query={pointTableQuery}
                                    />
                                </ErrorBoundary>
                            </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            props.dispatch(
                                CloseEventListDialogAction.create()
                            )
                        }>
                        Close
                    </button>
                </div>
            </Modal>
        );
    } else {
        return null;
    }
}

function mapStateToProps(state: AnalyseState) {

    let pointEvents = state.core.peaksJs ? state.core.peaksJs.points.getPoints() as Array<PeaksPoint> : [] as Array<PeaksPoint>;

    //Don't assign peaks point internal points to react internal state.
    pointEvents = pointEvents.map(pp => {
        return {
            timestamp: pp.timestamp,
            rawTimestamp: state.core.rawTimestampPoints.get(pp.id),
            color: pp.color,
            editable: pp.editable,
            eventTypeId: pp.eventTypeId,
            eventTypeName: pp.eventTypeName,
            id: pp.id,
            isPoint: pp.isPoint,
            labelText: pp.labelText,
            layerPosition: pp.layerPosition,
            editMode: pp.editMode
        } as PeaksPoint;
    });

    let periodEvents = state.core.peaksJs ? state.core.peaksJs.segments.getSegments() as Array<PeaksPeriod> : [] as Array<PeaksPeriod>;

    periodEvents = periodEvents.map(pp => {
        return {
            color: pp.color,
            editable: pp.editable,
            eventTypeId: pp.eventTypeId,
            eventTypeName: pp.eventTypeName,
            id: pp.id,
            isPoint: pp.isPoint,
            labelText: pp.labelText,
            layerPosition: pp.layerPosition,
            editMode: pp.editMode,
            startTime: pp.startTime,
            endTime: pp.endTime
        } as PeaksPeriod;
    })

    return {
        isVisible: state.core.isEventListOpen,
        pointEvents,
        periodEvents,
        // HACK: This is bad, but since Peaks updates its own state the component isn't aware
        // that the events have been removed so doesn't update the table state
        tableReRenderHack: Math.random().toString()
    };
}

export default connect<EventListProps, any, void>(mapStateToProps)(EventList);
